import { FieldProps, getIn } from 'formik';
import { FocusEvent, FocusEventHandler, KeyboardEvent, KeyboardEventHandler } from 'react';
import { useCheckoutState } from '../../utils/checkoutState';
import { SubstitutionOptions } from '../../utils/config';
import { isEmpty } from '../../utils/getValidator';
import ErrorMessage from '../atoms/ErrorMessage';
import Input, { InputProps } from '../atoms/Input';

export type TextFieldProps = FieldProps &
  InputProps & {
    label: string;
    formatOnBlur?: SubstitutionOptions;
  };

const TextField = ({
  field,
  form: { touched, errors, submitForm, setFieldValue },
  meta,
  label,
  formatOnBlur,
  ...props
}: TextFieldProps) => {
  const { isLoading } = useCheckoutState();
  const isTouched = getIn(touched, field.name);
  const error = isTouched && getIn(errors, field.name);
  const hasSuccess =
    isTouched && !error && !isEmpty(typeof field.value === 'string' ? field.value.trim() : field.value);

  const formatField = (e: FocusEvent<HTMLInputElement> | KeyboardEvent<HTMLInputElement>) => {
    // Take value from target in browser-autofill cases
    const trimmedField = (e.currentTarget ?? e.target).value.trim();
    setFieldValue(
      field.name,
      formatOnBlur ? trimmedField.replace(new RegExp(formatOnBlur.input), formatOnBlur.output) : trimmedField
    );
  };

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = e => {
    if (!isLoading && e.key === 'Enter') {
      formatField(e);
      submitForm();
    }
  };

  const onBlur: FocusEventHandler<HTMLInputElement> = e => {
    props.onBlur?.(e);
    field.onBlur(e);
    formatField(e);
  };

  return (
    <>
      <Input
        placeholder={label}
        hasError={!!error}
        hasSuccess={hasSuccess}
        onKeyDown={onKeyDown}
        {...props}
        {...field}
        onBlur={onBlur}
      />
      <ErrorMessage label={label} error={error} />
    </>
  );
};

export default TextField;
